import { cloneDeep } from 'lodash';

import { ASSET_TYPE, FIELD_VALUES } from '@funnels/constants';
import { Asset, PropertyAsset } from '@funnels/types';

interface ReturnValue {
  shouldUpsertProperties: boolean;
  expenseUpdatedProperties: Asset[];
}

export function getExpenseUpdatedPropertiesByDeleteProperty(asset: PropertyAsset, assets: Asset[]): ReturnValue {
  // set default return
  const defaultReturn = { shouldUpsertProperties: false, expenseUpdatedProperties: [] };
  // handle empty param
  if (!asset || !assets || assets.length <= 0) return defaultReturn;

  let shouldUpsertProperties: boolean = false;
  const properties = assets.filter((asset: Asset) => asset.type === ASSET_TYPE.PROPERTY) as PropertyAsset[]; // get all property asset
  // get all occupied properties - we don't need to update expenses for investment property
  const expenseUpdatedProperties = cloneDeep(
    properties.filter(
      (property: PropertyAsset) =>
        property.primaryPurpose === FIELD_VALUES.OWNER_OCCUPIED && property.clientId !== asset.clientId
    )
  );

  if (expenseUpdatedProperties.length <= 0) return defaultReturn; // this means no property need to be updated

  // if deleted property is owner occupied, clean all values of other occupied properties' expenses
  expenseUpdatedProperties.forEach((property: PropertyAsset) => {
    if (!property.propertyExpenses) return;
    (property.propertyExpenses || []).forEach((expense) => {
      if (!shouldUpsertProperties) shouldUpsertProperties = true;
      expense.amount = undefined;
    });
  });

  return { shouldUpsertProperties, expenseUpdatedProperties };
}

export function getAllAssetsUpdatedByDeleteProperties(
  assetsToBeDeleted: PropertyAsset[],
  assets: Asset[]
): ReturnValue {
  const formAssets = cloneDeep(assets);
  // go through all to be deleted assets, if any occupied property inside (maybe multiple primary property), get updated properties
  const occupiedProperty = assetsToBeDeleted.find(
    (asset) => asset.type === ASSET_TYPE.PROPERTY && asset.primaryPurpose === FIELD_VALUES.OWNER_OCCUPIED
  );
  if (!occupiedProperty) return { shouldUpsertProperties: false, expenseUpdatedProperties: [] };
  const { shouldUpsertProperties, expenseUpdatedProperties } = getExpenseUpdatedPropertiesByDeleteProperty(
    occupiedProperty,
    formAssets
  );
  // Merge the updated properties back to form value
  if (shouldUpsertProperties) {
    // get positions of all updated properties in formAssets
    const assetIndexesNeedToBeUpdated: number[] = expenseUpdatedProperties.map((asset) =>
      formAssets.findIndex((formAsset) => formAsset.clientId === asset.clientId)
    );
    // update formAssets
    expenseUpdatedProperties.forEach((expenseUpdatedProperty, index) => {
      const toBeUpdatedFormAssetIndex = assetIndexesNeedToBeUpdated[index];
      formAssets[toBeUpdatedFormAssetIndex] = expenseUpdatedProperty; // formAssets is deep clone, safely to be update
    });
  }
  return { shouldUpsertProperties, expenseUpdatedProperties: formAssets };
}
